import GlossaryTable from "Components/Admin/AdminOtherDetails/AdminGlossary/GlossaryTable";
import { notifications } from "Components/StyleComponent/Notification";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBulkDeleteGlossaryMutation,
  useDeleteGlossaryMutation,
  useGetAllGlossaryQuery,
} from "Services/GlossaryApiSlice";
import { GlossaryFormItem } from "Types/Admin/GlossaryFormItem";
import { Input } from "@mantine/core";
import { Search } from "lucide-react";

const AdminGlossary: React.FC = () => {
  const navigate = useNavigate();
  const plant = process.env.REACT_APP_PLANT || "";
  const [search, setSearch] = useState("");

  const { data, isLoading, isError, refetch } = useGetAllGlossaryQuery({
    plant,
    search: search || undefined,
  });

  const [deleteGlossary] = useDeleteGlossaryMutation();
  const [bulkDeleteGlossary] = useBulkDeleteGlossaryMutation();

  const items: GlossaryFormItem[] =
    data?.results?.map((result: any) => {
      const hdr = result.glossaryHdr || {};

      // Map images with IDs from glossaryDetList
      interface ImageWithId {
        id: number;
        url: string;
      }

      const imagesWithIds: ImageWithId[] =
        result.glossaryDetList?.map((det: any) => ({
          id: det.id,
          url: det.glossaryImage,
        })) ?? [];

      // Keep preview URLs for backward compatibility
      const imagesPreviewUrls = imagesWithIds.map(
        (img: ImageWithId) => img.url,
      );

      return {
        id: hdr.id,
        date: hdr.glossaryDate
          ? new Date(hdr.glossaryDate).toLocaleDateString("en-GB")
          : "",
        title: hdr.glossaryName ?? "",
        venue: hdr.address ?? hdr.city ?? "",
        glossaryDescription: hdr.glossaryDescription ?? "",
        glossaryDescription2: hdr.glossaryDescription2 ?? "",
        address: hdr.address ?? "",
        city: hdr.city ?? "",
        state: hdr.state ?? "",
        country: hdr.country ?? "",
        pin: hdr.pin ?? "",
        // Store both formats
        imagesPreviewUrls,
        imagesWithIds,
        section1Title: hdr.section1Title ?? "",
        section1Description: hdr.section1Description ?? "",
        section2Title: hdr.section2Title ?? "",
        section2Description: hdr.section2Description ?? "",
        section3Title: hdr.section3Title ?? "",
        section3Description: hdr.section3Description ?? "",
        section4Title: hdr.section4Title ?? "",
        section4Description: hdr.section4Description ?? "",
        section5Title: hdr.section5Title ?? "",
        section5Description: hdr.section5Description ?? "",
        section6Title: hdr.section6Title ?? "",
        section6Description: hdr.section6Description ?? "",
        section7Title: hdr.section7Title ?? "",
        section7Description: hdr.section7Description ?? "",
        section8Title: hdr.section8Title ?? "",
        section8Description: hdr.section8Description ?? "",
      };
    }) ?? [];

  const handleView = (item: GlossaryFormItem) => {
    navigate(`/admin/other-details/glossary/view/${item.id}`, {
      state: { item },
    });
  };

  const handleEdit = (item: GlossaryFormItem) => {
    navigate(`/admin/other-details/glossary/edit/${item.id}`, {
      state: { item, mode: "edit" as const },
    });
  };

  const handleDelete = async (id: number) => {
    try {
      const response: any = await deleteGlossary({ id, plant }).unwrap();

      notifications.show({
        message: response?.message,
        color: "green",
      });

      refetch();
    } catch (err: any) {
      console.error("Delete error", err);

      notifications.show({
        message: err?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleBulkDelete = async (ids: number[]) => {
    try {
      const response: any = await bulkDeleteGlossary({ ids, plant }).unwrap();

      notifications.show({
        message: response?.message,
        color: "green",
      });

      refetch();
    } catch (err: any) {
      console.error("Bulk delete error", err);

      notifications.show({
        message: err?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleAdd = () => {
    navigate("/admin/other-details/glossary/add");
  };

  return (
    <div className="px-4 sm:px-8 py-6 pb-24 min-h-screen">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 max-w-6xl mx-auto">
        <h3 className="text-base font-medium">Gallery</h3>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Input
            placeholder="Search by gallery title"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftSection={<Search size={16} />}
            className="w-full sm:w-[300px]"
          />

          <button
            onClick={handleAdd}
            className="bg-blue-600 text-white text-sm px-4 py-2 rounded shadow hover:bg-blue-700 whitespace-nowrap self-end sm:self-auto"
          >
            + Add Gallery
          </button>
        </div>
      </div>

      <div className="bg-white p-4 sm:p-5 rounded shadow-sm w-full max-w-6xl mx-auto min-h-[260px]">
        {isLoading && (
          <div className="flex items-center justify-center min-h-[200px]">
            <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {isError && (
          <p className="text-red-500 text-center py-8">
            Failed to load glossary. Please try again.
          </p>
        )}

        {!isLoading && !isError && (
          <GlossaryTable
            items={items}
            onEdit={handleEdit}
            onView={handleView}
            onDelete={handleDelete}
            onBulkDelete={handleBulkDelete}
            showBulkActions={true}
          />
        )}
      </div>
    </div>
  );
};

export default AdminGlossary;
